// Parse an EEX contract "period" string into delivery start/end dates and the
// notional delivery hours ("MWh std" — hours for a 1 MW position). Hours are
// DST-aware for CET/CEST (EU rules: spring-forward = last Sunday of March → a
// 23-hour day; fall-back = last Sunday of October → a 25-hour day), so e.g.
// Q4 = 2209h and Q1 = 2159h fall out automatically — no hardcoded overrides.
//
// Handles month (date), quarter, week (ISO), year, single day, weekend, and
// custom windows ("15/08/26-20/09/26", bilateral trades' arbitrary delivery
// ranges). Spreads ("a x b") and anything unrecognized return null (caller
// shows "—"). Pure — no DB, unit-testable.
const DAY_MS = 86_400_000;
const MONTH_ABBR = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const pad = (n) => String(n).padStart(2, "0");
const iso = (ms) => {
    const d = new Date(ms);
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};
function lastSundayOfMonth(year, monthIndex0) {
    const lastDay = Date.UTC(year, monthIndex0 + 1, 0); // day 0 of next month = last day
    const dow = new Date(lastDay).getUTCDay(); // 0 = Sunday
    return lastDay - dow * DAY_MS;
}
// DST-aware hour count for an inclusive [start, end] date range in CET/CEST.
export function deliveryHours(startMs, endMs) {
    const days = Math.round((endMs - startMs) / DAY_MS) + 1;
    let adj = 0;
    const startYear = new Date(startMs).getUTCFullYear();
    const endYear = new Date(endMs).getUTCFullYear();
    for (let y = startYear; y <= endYear; y++) {
        const spring = lastSundayOfMonth(y, 2); // March
        const fall = lastSundayOfMonth(y, 9); // October
        if (spring >= startMs && spring <= endMs)
            adj -= 1; // 23-hour day
        if (fall >= startMs && fall <= endMs)
            adj += 1; // 25-hour day
    }
    return days * 24 + adj;
}
function build(startMs, endMs, kind) {
    return { start: iso(startMs), end: iso(endMs), hours: deliveryHours(startMs, endMs), kind };
}
function isoWeekMonday(year, week) {
    const jan4 = Date.UTC(year, 0, 4);
    const dow = (new Date(jan4).getUTCDay() + 6) % 7; // 0 = Monday
    const week1Monday = jan4 - dow * DAY_MS;
    return week1Monday + (week - 1) * 7 * DAY_MS;
}
// Date.UTC silently rolls over out-of-range components ("32/13/26" would become
// a real date) — round-trip the parts so an invalid calendar date is rejected.
function utcDateStrict(y, mo1, d) {
    const ms = Date.UTC(y, mo1 - 1, d);
    const dt = new Date(ms);
    return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo1 - 1 && dt.getUTCDate() === d
        ? ms
        : null;
}
// Custom delivery window (bilateral trades): "15/08/26-20/09/26" or ISO
// "2026-08-15..2026-09-20", both inclusive. Null when either date is invalid
// or the range is inverted; undefined means this is not a range.
function parseCustomRange(p) {
    let start = null;
    let end = null;
    const mDmy = /^(\d{1,2})\/(\d{1,2})\/(\d{2})\s*-\s*(\d{1,2})\/(\d{1,2})\/(\d{2})$/.exec(p);
    if (mDmy) {
        start = utcDateStrict(2000 + +mDmy[3], +mDmy[2], +mDmy[1]);
        end = utcDateStrict(2000 + +mDmy[6], +mDmy[5], +mDmy[4]);
    }
    else {
        const mIso = /^(\d{4})-(\d{2})-(\d{2})\s*\.\.\s*(\d{4})-(\d{2})-(\d{2})$/.exec(p);
        if (!mIso)
            return p.includes("..") ? null : undefined;
        start = utcDateStrict(+mIso[1], +mIso[2], +mIso[3]);
        end = utcDateStrict(+mIso[4], +mIso[5], +mIso[6]);
    }
    return start !== null && end !== null && end >= start ? { startMs: start, endMs: end } : null;
}
// A custom window that IS exactly a standard tenor must parse AS that tenor —
// "01/08/26-31/08/26" and "Aug-26" are the same delivery and must never split
// into two contract identities over spelling.
function standardKindOf(startMs, endMs) {
    const s = new Date(startMs);
    const e = new Date(endMs);
    if (s.getUTCDate() !== 1)
        return null;
    if (endMs !== Date.UTC(e.getUTCFullYear(), e.getUTCMonth() + 1, 0))
        return null;
    const months = (e.getUTCFullYear() - s.getUTCFullYear()) * 12 + (e.getUTCMonth() - s.getUTCMonth()) + 1;
    const sm = s.getUTCMonth(); // 0-based start month
    if (months === 1)
        return "month";
    if (months === 3 && sm % 3 === 0)
        return "quarter";
    if (months === 6 && (sm === 3 || sm === 9))
        return "season"; // Sum (Apr) / Win (Oct)
    if (months === 12 && sm === 0)
        return "year";
    return null;
}
export function parsePeriod(period) {
    const p = (period ?? "").trim();
    if (!p)
        return null;
    // Custom window — checked BEFORE the month-date grammar: the bare "YYYY-MM-DD"
    // prefix match below would otherwise swallow an ISO range as that date's month.
    const custom = parseCustomRange(p);
    if (custom !== undefined) {
        if (custom === null)
            return null;
        return build(custom.startMs, custom.endMs, standardKindOf(custom.startMs, custom.endMs) ?? "custom");
    }
    // Month: a date like "2025-06-01" (optionally with a time) → that whole month
    const mDate = /^(\d{4})-(\d{2})-(\d{2})(?:$|[ T])/.exec(p);
    if (mDate) {
        const y = +mDate[1];
        const mo = +mDate[2] - 1;
        if (utcDateStrict(y, mo + 1, +mDate[3]) === null)
            return null;
        return build(Date.UTC(y, mo, 1), Date.UTC(y, mo + 1, 0), "month");
    }
    // Month: a 3-letter abbreviation like "Jun-26" = June 2026 (EEX month label).
    // A spread ("Sep-25 x Oct-25") won't match — the $ anchor rejects the tail.
    const mMonthAbbr = /^([A-Za-z]{3})-(\d{2})$/.exec(p);
    if (mMonthAbbr) {
        const mo = MONTH_ABBR.indexOf(mMonthAbbr[1].toLowerCase());
        if (mo >= 0) {
            const y = 2000 + +mMonthAbbr[2];
            return build(Date.UTC(y, mo, 1), Date.UTC(y, mo + 1, 0), "month");
        }
    }
    // Quarter: "Q126" = Q1 2026, "Q425" = Q4 2025
    const mQ = /^Q([1-4])(\d{2})$/i.exec(p);
    if (mQ) {
        const q = +mQ[1];
        const y = 2000 + +mQ[2];
        const startMo = (q - 1) * 3;
        return build(Date.UTC(y, startMo, 1), Date.UTC(y, startMo + 3, 0), "quarter");
    }
    // Week: broker label with the delivery Monday, e.g. "Wk32 03/08/26".
    // Validate both the calendar date and the stated ISO week before treating it
    // as the same contract as the compact "Wk32-26" spelling.
    const mWDate = /^Wk(\d{1,2})\s+(\d{1,2})([/.])(\d{1,2})\3(\d{2}|\d{4})$/i.exec(p);
    if (mWDate) {
        const week = +mWDate[1];
        const d = +mWDate[2];
        const mo = +mWDate[4];
        const rawYear = +mWDate[5];
        const y = rawYear < 100 ? 2000 + rawYear : rawYear;
        const monday = utcDateStrict(y, mo, d);
        if (monday !== null &&
            new Date(monday).getUTCDay() === 1 &&
            isoWeekNumber(monday).week === week) {
            return build(monday, monday + 6 * DAY_MS, "week");
        }
        return null;
    }
    // Week: compact ISO label "Wk21-25"
    const mW = /^Wk(\d{1,2})-(\d{2})$/i.exec(p);
    if (mW) {
        const week = +mW[1];
        const y = 2000 + +mW[2];
        const monday = isoWeekMonday(y, week);
        return build(monday, monday + 6 * DAY_MS, "week");
    }
    // Gas season: "Win 25" / "Winter-25" = Oct-25..Mar-26; "Sum 26" = Apr..Sep 2026
    const mS = /^(win(?:ter)?|sum(?:mer)?)[\s-]?(\d{2}|\d{4})$/i.exec(p);
    if (mS) {
        const y = +mS[2] < 100 ? 2000 + +mS[2] : +mS[2];
        return /^w/i.test(mS[1])
            ? build(Date.UTC(y, 9, 1), Date.UTC(y + 1, 3, 0), "season")
            : build(Date.UTC(y, 3, 1), Date.UTC(y, 9, 0), "season");
    }
    // Year: "2026"
    const mY = /^(\d{4})$/.exec(p);
    if (mY) {
        const y = +mY[1];
        return build(Date.UTC(y, 0, 1), Date.UTC(y, 11, 31), "year");
    }
    // Single day: "Thu 26/06/25"
    const mDay = /^(?:mon|tue|wed|thu|fri|sat|sun)\s+(\d{1,2})\/(\d{1,2})\/(\d{2})$/i.exec(p);
    if (mDay) {
        const d = +mDay[1];
        const mo = +mDay[2] - 1;
        const y = 2000 + +mDay[3];
        const ms = utcDateStrict(y, mo + 1, d);
        if (ms === null)
            return null;
        return build(ms, ms, "day");
    }
    // Weekend: "WkEnd 16-17/08/25"
    const mWe = /^WkEnd\s+(\d{1,2})-(\d{1,2})\/(\d{1,2})\/(\d{2})$/i.exec(p);
    if (mWe) {
        const d1 = +mWe[1];
        const d2 = +mWe[2];
        const mo = +mWe[3] - 1;
        const y = 2000 + +mWe[4];
        const start = utcDateStrict(y, mo + 1, d1), end = utcDateStrict(y, mo + 1, d2);
        if (start === null || end === null || end < start)
            return null;
        return build(start, end, "weekend");
    }
    // Spread ("a x b") and anything else: not a single delivery length.
    return null;
}
const MONTH_LABEL = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// ISO week number of a date already known to be that week's Monday.
function isoWeekNumber(mondayMs) {
    const thursday = mondayMs + 3 * DAY_MS; // the year is decided by the Thursday
    const year = new Date(thursday).getUTCFullYear();
    const week1Monday = isoWeekMonday(year, 1);
    return { week: Math.round((mondayMs - week1Monday) / (7 * DAY_MS)) + 1, year };
}
// A stable, re-parseable label for the SAME contract regardless of how its
// period was spelled — e.g. "Jun-26" and "2026-06-01 00:00:00" both canonicalize
// to "Jun-26", "Q226"/"Q2-26" to "Q226". Used to group positions and match
// pair allocations so mixed spellings (paste vs archive) don't split a contract.
// Unparseable periods (spreads, odd day/weekend strings) return the trimmed raw,
// so nothing is ever merged that isn't provably the same delivery.
export function canonicalPeriod(period) {
    const raw = (period ?? "").trim();
    const parsed = parsePeriod(raw);
    if (!parsed)
        return raw;
    const y = +parsed.start.slice(0, 4);
    const mo = +parsed.start.slice(5, 7);
    const yy = pad(y % 100);
    switch (parsed.kind) {
        case "month":
            return `${MONTH_LABEL[mo - 1]}-${yy}`;
        case "quarter":
            return `Q${Math.floor((mo - 1) / 3) + 1}${yy}`;
        case "year":
            return String(y);
        case "season":
            return `${mo === 10 ? "Win" : "Sum"} ${yy}`;
        case "week": {
            const { week, year } = isoWeekNumber(Date.UTC(y, mo - 1, +parsed.start.slice(8, 10)));
            return `Wk${pad(week)}-${pad(year % 100)}`;
        }
        // Custom windows canonicalize to the dd/mm/yy range spelling (re-parseable;
        // whole-tenor ranges never reach here — parsePeriod already collapsed them).
        case "custom": {
            const dmy = (isoDate) => `${isoDate.slice(8, 10)}/${isoDate.slice(5, 7)}/${isoDate.slice(2, 4)}`;
            return `${dmy(parsed.start)}-${dmy(parsed.end)}`;
        }
        // day/weekend rarely form positions and their date strings would collide
        // with the month-date grammar — leave them as the raw spelling.
        default:
            return raw;
    }
}
// Free-text blotter search over a stored period. Matches case-insensitively
// against EITHER the raw stored spelling OR its canonical label, so a monthly
// stored as "2026-04-01 00:00:00" is found by both "Apr-26" (the label the UI
// shows) and "2026-04" (the raw date). An empty/whitespace query matches all.
export function periodMatches(stored, query) {
    const needle = query.trim().toLowerCase();
    if (!needle)
        return true;
    const raw = (stored ?? "").toLowerCase();
    return raw.includes(needle) || canonicalPeriod(stored).toLowerCase().includes(needle);
}
// Where a contract stands relative to its delivery window on `todayIso`
// (YYYY-MM-DD, the CET calendar day — pass the route's Europe/Berlin "today").
// Null when the period is unparseable (spreads, odd strings): unknown, not
// assumed forward. ISO strings compare lexicographically, so plain < / > work.
export function deliveryLifecycle(p, todayIso) {
    if (!p)
        return null;
    if (todayIso > p.end)
        return "expired";
    if (todayIso < p.start)
        return "forward";
    return "in_delivery";
}
//# sourceMappingURL=period.js.map